<template>
  <v-container fluid class="sci-page-wrapper pa-0" style="font-family: 'Inter', sans-serif;">
    <div class="p-4 md:p-8">
      <div class="max-w-4xl mx-auto">
        
        <!-- HEADER -->
      <div class="sci-page-header pb-4">
          <div>
            <h1 class="sci-page-title-only">Fornecedor | Almoxarifado</h1>
            <p class="text-sm text-slate-500 font-medium italic">{{ id ? 'Editando informações do fornecedor' : 'Cadastrando novo fornecedor no almoxarifado' }}</p>
          </div>
          <v-btn
            @click="$router.push('/parts-and-services/providers-manager')"
            text
            class="text-slate-400 font-bold hover:text-slate-600"
          >
            <v-icon left>mdi-arrow-left</v-icon>
            VOLTAR
          </v-btn>
        </div>

        <v-form ref="form" v-model="valid" lazy-validation>
          <div class="space-y-6">
            
            <!-- SEÇÃO: DADOS BÁSICOS -->
            <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
              <div class="px-6 py-3 flex items-center gap-3" style="background-color: #333333;">
                <v-icon color="orange" small>mdi-account-details-outline</v-icon>
                <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">DADOS BÁSICOS</h2>
              </div>
              
              <div class="p-6">
                <div class="flex flex-col md:flex-row gap-8 items-start mb-6">
                  <!-- LOGOTIPO -->
                  <div class="flex flex-col items-center gap-3">
                    <div 
                      @click="$refs.fileInput.click()"
                      class="relative group cursor-pointer"
                    >
                      <v-avatar size="140" class="border-2 border-slate-100 bg-slate-50 overflow-hidden transition-all group-hover:border-orange-200">
                        <img 
                          :src="data.provider_logo ? $store.state.urlImages + data.provider_logo : require('@/assets/no-image.jpg')" 
                          alt="Logo"
                          class="object-cover"
                        >
                        <div class="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
                          <v-icon color="white">mdi-camera-outline</v-icon>
                        </div>
                      </v-avatar>
                    </div>
                    <span class="text-[10px] font-black text-slate-400 uppercase tracking-tighter">Clique para alterar</span>
                    <input type="file" ref="fileInput" class="hidden" accept="image/*" @change="uploadImage">
                  </div>

                  <!-- CAMPOS PRINCIPAIS -->
                  <div class="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div class="md:col-span-2">
                       <v-checkbox 
                        v-model="data.provider_r_type"
                        label="CADASTRO PESSOA JURÍDICA (PJ)"
                        color="orange"
                        hide-details
                        class="mt-0 pt-0 mb-4"
                      />
                    </div>

                    <div class="md:col-span-2">
                      <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">NOME / RAZÃO SOCIAL</label>
                      <v-text-field
                        v-model="data.provider_name"
                        :rules="fieldRules"
                        placeholder="NOME COMPLETO OU RAZÃO SOCIAL"
                        solo flat dense hide-details
                        class="custom-select"
                      />
                    </div>

                    <div>
                      <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">{{ data.provider_r_type ? 'CNPJ' : 'CPF' }}</label>
                      <v-text-field
                        v-model="data.provider_document"
                        @input="data.provider_r_type ? setCnpj(data.provider_document) : setCpf(data.provider_document)"
                        :placeholder="data.provider_r_type ? '00.000.000/0000-00' : '000.000.000-00'"
                        solo flat dense hide-details
                        class="custom-select"
                        :error="data.provider_r_type ? !validCnpj(data.provider_document) : !validCpf(data.provider_document)"
                      />
                    </div>

                    <div>
                      <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">{{ data.provider_r_type ? 'INSCRIÇÃO ESTADUAL' : 'RG' }}</label>
                      <v-text-field
                        v-model="data.provider_register"
                        placeholder="DOC. IDENTIFICAÇÃO"
                        solo flat dense hide-details
                        class="custom-select"
                      />
                    </div>
                  </div>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6 border-t border-slate-100">
                  <div class="md:col-span-2">
                    <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">E-MAIL</label>
                    <v-text-field
                      v-model="data.provider_mail"
                      placeholder="EXEMPLO@EMAIL.COM"
                      solo flat dense hide-details
                      class="custom-select lowercase"
                    />
                  </div>
                  <div v-if="data.provider_r_type">
                    <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">INSCRIÇÃO MUNICIPAL</label>
                    <v-text-field
                      v-model="data.provider_im"
                      placeholder="INSCRIÇÃO MUNICIPAL"
                      solo flat dense hide-details
                      class="custom-select"
                    />
                  </div>
                  <div>
                    <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">TELEFONE</label>
                    <v-text-field
                      v-model="data.provider_tel"
                      v-mask-phone.br
                      placeholder="(00) 0000-0000"
                      solo flat dense hide-details
                      class="custom-select"
                    />
                  </div>
                  <div>
                    <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">CELULAR / WHATSAPP</label>
                    <v-text-field
                      v-model="data.provider_cel"
                      v-mask-phone.br
                      placeholder="(00) 0 0000-0000"
                      solo flat dense hide-details
                      class="custom-select text-orange-600"
                    />
                  </div>
                  <div>
                    <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">TIPO DE ATENDIMENTO</label>
                    <v-autocomplete
                      v-model="data.attendance_id"
                      :items="attendances"
                      item-value="attendance_id"
                      item-text="attendance_desc"
                      placeholder="SELECIONE..."
                      solo flat dense hide-details
                      class="custom-select"
                    />
                  </div>
                </div>
              </div>
            </section>

            <!-- SEÇÃO: LOCALIZAÇÃO -->
            <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
              <div class="px-6 py-3 flex items-center gap-3" style="background-color: #333333;">
                <v-icon color="orange" small>mdi-map-marker-outline</v-icon>
                <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">LOCALIZAÇÃO</h2>
              </div>
              
              <div class="p-6 grid grid-cols-1 md:grid-cols-12 gap-6">
                <div class="md:col-span-3">
                  <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">CEP</label>
                  <v-text-field
                    v-model="data.provider_postal_code"
                    v-mask-cep
                    @blur="getCep(data.provider_postal_code)"
                    placeholder="00.000-000"
                    solo flat dense hide-details
                    :loading="loader"
                    class="custom-select"
                  />
                </div>
                <div class="md:col-span-7">
                  <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">LOGRADOURO</label>
                  <v-text-field
                    v-model="data.provider_street"
                    placeholder="AVENIDA, RUA, ETC"
                    solo flat dense hide-details
                    class="custom-select"
                  />
                </div>
                <div class="md:col-span-2">
                  <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">NÚMERO</label>
                  <v-text-field
                    v-model="data.provider_number"
                    placeholder="000"
                    solo flat dense hide-details
                    class="custom-select"
                  />
                </div>
                <div class="md:col-span-4">
                  <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">BAIRRO</label>
                  <v-text-field
                    v-model="data.provider_district"
                    placeholder="BAIRRO"
                    solo flat dense hide-details
                    class="custom-select"
                  />
                </div>
                <div class="md:col-span-4">
                  <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">CIDADE</label>
                  <v-text-field
                    v-model="data.provider_city"
                    placeholder="CIDADE"
                    solo flat dense hide-details
                    class="custom-select"
                  />
                </div>
                <div class="md:col-span-2">
                  <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">ESTADO</label>
                  <v-text-field
                    v-model="data.provider_state"
                    placeholder="UF"
                    solo flat dense hide-details
                    class="custom-select"
                  />
                </div>
                <div class="md:col-span-2">
                  <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">COMPLEMENTO</label>
                  <v-text-field
                    v-model="data.provider_complement"
                    placeholder="SALA, APT"
                    solo flat dense hide-details
                    class="custom-select"
                  />
                </div>
              </div>
            </section>

            <!-- BOTÕES DE AÇÃO -->
            <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
              <div class="bg-slate-50 px-8 py-5 border-t border-slate-200 flex justify-end items-center gap-4">
                <button 
                  @click="$router.push('/parts-and-services/providers-manager')"
                  class="text-slate-500 hover:text-slate-700 font-bold text-[11px] uppercase px-4 py-2 transition-colors flex-none"
                >
                  Cancelar
                </button>
                <div class="flex-none">
                  <button
                    @click="validate"
                    :disabled="!valid || loader"
                    class="bg-orange-500 hover:bg-orange-600 text-white font-bold text-[11px] px-6 py-2.5 rounded shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all w-auto max-w-max whitespace-nowrap flex items-center gap-2"
                  >
                    <v-icon small color="white" class="mr-1" style="font-size: 14px;">mdi-check</v-icon>
                    <span v-if="loader" class="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></span>
                    <span v-else>{{ id ? 'SALVAR ALTERAÇÕES' : 'CONFIRMAR CADASTRO' }}</span>
                  </button>
                </div>
              </div>
            </section>

          </div>
        </v-form>
      </div>
    </div>

    <!-- NOTIFICAÇÕES -->
    <v-snackbar multi-line border="left" :color="snack.color" v-model="snack.show" transition="scroll-y-reverse-transition" class="mb-6">
      <div class="flex items-center gap-3">
        <v-icon color="white">mdi-information-outline</v-icon>
        <span class="font-bold text-xs uppercase tracking-wider">{{ snack.message }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn dark text v-bind="attrs" @click="snack.show = false">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

  </v-container>
</template>

<script>
export default {
  props: ["id"],
  data: () => ({
    valid: false,
    loader: false,
    attendances: [],
    fieldRules: [(v) => !!v || "Este campo é requerido"],
    snack: {
      show: false,
      message: "",
      color: "success",
    },
    data: {
      provider_id: null,
      attendance_id: null,
      provider_logo: "",
      provider_type: "",
      provider_name: "",
      provider_document: "",
      provider_register: "",
      provider_im: "",
      provider_mail: "",
      provider_cel: "",
      provider_tel: "",
      provider_postal_code: "",
      provider_street: "",
      provider_number: "",
      provider_complement: "",
      provider_district: "",
      provider_city: "",
      provider_state: "",
      provider_r_type: null,
    },
  }),
  created() {
    this.$http
      .get("/settings/attendance_types")
      .then((r) => (this.attendances = r.data));
    if (this.id) {
      this.$store.dispatch("setLoader", true);
      this.$http.get(`/parts-and-services/providers/${this.id}`).then((r) => {
        this.data = r.data[0];
        delete this.data.created_at;
        this.$store.dispatch("setLoader", false);
      });
    }
  },
  methods: {
    setCnpj(v) {
      if (!v) return;
      v = v.replace(/\D/g, "");
      v = v.replace(/^(\d{2})(\d)/, "$1.$2");
      v = v.replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3");
      v = v.replace(/\.(\d{3})(\d)/, ".$1/$2");
      v = v.replace(/(\d{4})(\d)/, "$1-$2");
      this.data.provider_document = v;
    },
    setCpf(v) {
      if (!v) return;
      v = v.replace(/\D/g, "");
      v = v.replace(/(\d{3})(\d)/, "$1.$2");
      v = v.replace(/(\d{3})(\d)/, "$1.$2");
      v = v.replace(/(\d{3})(\d{1,2})$/, "$1-$2");
      this.data.provider_document = v;
    },
    validCnpj(v) {
      if (!v) return false;
      let cnpj = v.replace(/\D/gim, "");
      if (cnpj.length !== 14) return false;
      if (/^(\d)\1+$/.test(cnpj)) return false;
      let t = cnpj.length - 2,
        d = cnpj.substring(t),
        d1 = parseInt(d.charAt(0)),
        d2 = parseInt(d.charAt(1)),
        calc = (x) => {
          let n = cnpj.substring(0, x),
            y = x - 7,
            s = 0;
          for (let i = x; i >= 1; i--) {
            s += n.charAt(x - i) * y--;
            if (y < 2) y = 9;
          }
          let r = 11 - (s % 11);
          return r > 9 ? 0 : r;
        };
      return calc(t) === d1 && calc(t + 1) === d2;
    },
    validCpf(cpf) {
      if (!cpf) return false;
      cpf = cpf.replace(/\D/g, "");
      if (cpf.length != 11 || /^(\d)\1{10}$/.test(cpf)) return false;
      var result = true;
      [9, 10].forEach(function (j) {
        var soma = 0, r;
        cpf.split(/(?=)/).splice(0, j).forEach(function (e, i) {
          soma += parseInt(e) * (j + 2 - (i + 1));
        });
        r = soma % 11;
        r = r < 2 ? 0 : 11 - r;
        if (r != cpf.substring(j, j + 1)) result = false;
      });
      return result;
    },
    validate() {
      if (this.$refs.form.validate()) {
        this.createOrUpdate();
      } else {
        this.snack.show = true;
        this.snack.color = "warning";
        this.snack.message = "Verifique os campos obrigatórios!";
      }
    },
    createOrUpdate() {
      this.loader = true;
      this.$store.dispatch("setLoader", true);
      const httpMethod = this.id ? 'put' : 'post';
      const url = this.id ? `/parts-and-services/providers/${this.id}` : "/parts-and-services/providers";
      
      this.$http[httpMethod](url, this.data)
        .then((r) => {
          this.snack.show = true;
          this.snack.message = r.data.result || "Operação realizada com sucesso!";
          this.snack.color = "success";
          setTimeout(() => {
            this.$router.push("/parts-and-services/providers-manager");
          }, 1500);
        })
        .catch((e) => {
          this.snack.show = true;
          this.snack.message = e.message;
          this.snack.color = "error";
          this.loader = false;
          this.$store.dispatch("setLoader", false);
        });
    },
    uploadImage(event) {
      this.$store.dispatch("setLoader", true);
      const fd = new FormData();
      fd.append("image", event.target.files[0], event.target.files[0].name);
      this.$http
        .post("/upload", fd)
        .then((r) => {
          this.data.provider_logo = r.data.cover;
          this.$store.dispatch("setLoader", false);
        })
        .catch((e) => {
          alert("Erro no upload da imagem: " + e.message);
          this.$store.dispatch("setLoader", false);
          event.target.files = [];
        });
    },
    getCep(cep) {
      if (cep && cep.length >= 8) {
        this.loader = true;
        const cleanCep = cep.replace(/\D/g, "");
        this.$http
          .get("https://viacep.com.br/ws/" + cleanCep + "/json/")
          .then((response) => {
            if (!response.data.erro) {
              this.data.provider_street = response.data.logradouro;
              this.data.provider_district = response.data.bairro;
              this.data.provider_city = response.data.localidade;
              this.data.provider_state = response.data.uf;
            }
            this.loader = false;
          })
          .catch(() => {
            this.loader = false;
          });
      }
    },
  },
};
</script>

<style scoped>
.custom-select ::v-deep .v-input__slot {
  background: #f8fafc !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: none !important;
  border-radius: 12px !important;
  height: 48px !important;
  padding: 0 16px !important;
}

.custom-select ::v-deep input {
  font-size: 14px !important;
  font-weight: 600 !important;
  color: #334155 !important;
  text-transform: uppercase !important;
}

.custom-select ::v-deep .v-label {
  font-size: 10px !important;
  font-weight: 900 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.15em !important;
  color: #94a3b8 !important;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.animate-spin {
  animation: spin 1s linear infinite;
}
</style>
